import { Injectable, inject } from "@angular/core";
import { Observable, ReplaySubject, Subject } from "rxjs";
import { map } from "rxjs/operators";
import { Forsorjningskalla, IForsorjningskalla } from "~app/common/models/forsorjningskalla";
import { ITypeAheadResult } from "~app/common/models/type-ahead-result.model";
import { ApiClientService } from "~app/common/services/api-client.service";
import { Utils } from "~app/common/services/utils";
import { ILeveransrad, ILeverantorsorderDetailGrunddata, ILeverantorsorderListResponse, IUppdatateraPeriodLeveransraderResponse } from "../models/leverantororder-models";
import { ILeverantorsorderDetail, ILeverantorsorderDetailRad } from "../models/leverantorsorder-detail-model";
import { ILeverantorsorderListRequest } from "../models/leverantorsorder-list-model";

@Injectable({
    providedIn: "root"
})
export class LeverantorsorderService {
    private readonly apiClient = inject(ApiClientService);

    private readonly grunddataObservable = new ReplaySubject<ILeverantorsorderDetailGrunddata>(1);
    private lastGrunddataFetch = 0;
    searchForLeverantorTypeAhead(query: { id?: number; query?: string }): Observable<ITypeAheadResult[]> {
        return this.apiClient
            .post<any, ITypeAheadResult[]>(`Leverantorsorder/SearchForLeverantorByHkInternnrNamnPostortTypeahead`, query, false, null, ApiClientService.JsonHeader);
    }

    searchForOrdernummerTypeahead(query: { id?: number; query?: string }): Observable<ITypeAheadResult[]> {
        const subject = new Subject<ITypeAheadResult[]>();
        if (!Utils.isDefined(query.id) && query.query.length < 3) {
            return subject.asObservable();
        }

        return this.apiClient.post<any, ITypeAheadResult[]>(`typeahead/leverantorsorderordernr`, query, false, null, ApiClientService.JsonHeader);
    }

    searchForAvtalsansvarigTypeahead(query: { id?: number; query?: string }): Observable<ITypeAheadResult[]> {
        const subject = new Subject<ITypeAheadResult[]>();
        if (!Utils.isDefined(query.id) && query.query.length < 3) {
            return subject.asObservable();
        }

        return this.apiClient.post<any, ITypeAheadResult[]>(`typeahead/anvandare`, query, false, null, ApiClientService.JsonHeader);
    }

    searchForsorjningskalla(request: any, exclude: (fk: IForsorjningskalla) => boolean): Observable<any> {
        const include = (x: IForsorjningskalla) => !exclude(x);
        return this.apiClient.post<any, IForsorjningskalla[]>("Leverantorsorder/SearchForForsorjningskallor", request)
            .pipe(
                map(result => result.filter(include))
            );
    }

    getKundorderradInfo(id: number): Observable<Forsorjningskalla> {
        return this.apiClient.get<Forsorjningskalla>(`Leverantorsorder/GetExtraKundorderradinfoById/${id}`);
    }

    searchForMottagningsplatsTypeAhead(query: { input?: string; id?: number }): Observable<ITypeAheadResult[]> {
        const subject = new Subject<ITypeAheadResult[]>();
        if (!Utils.isDefined(query.id) && query.input.length < 3) {
            return subject.asObservable();
        }

        return this.apiClient.post<any, ITypeAheadResult[]>(`typeahead/mottagningsplats`, query, false, null, ApiClientService.JsonHeader);
    }

    searchForLeverantorsorder(leverantorsorderSearchParams: ILeverantorsorderListRequest): Observable<ILeverantorsorderListResponse> {
        return this.apiClient
            .post<any, ILeverantorsorderListResponse>(`Leverantorsorder/SearchForleverantorsorder`, leverantorsorderSearchParams, false, null, ApiClientService.JsonHeader);
    }

    getBalans(leveransrader: ILeveransrad[], kvantitet: number) {
        let summaForsorjningsrader = 0;
        if (leveransrader && leveransrader.length) {
            summaForsorjningsrader = leveransrader.reduce<number>((a, b) => (a + b.forsorjningsrader.reduce((c, d) => (c + d.dagkvantitet), 0)), 0);
        }

        return (Math.round(kvantitet - summaForsorjningsrader) + 0);
    }

    updatePeriodLeverantorsorderrad(leveransrader: ILeveransrad[], dateFrom: Date, dateTo: Date): Observable<IUppdatateraPeriodLeveransraderResponse> {

        const request = {} as any;
        request.leveransrader = leveransrader;
        request.periodStart = dateFrom;
        request.periodSlut = dateTo;
        return this.apiClient.post<any, any>("Leverantorsorder/UppdateraPeriodLeveransrader", request);
    }

    getLeverantorsorderGrunddata(maxAge = 60): Observable<ILeverantorsorderDetailGrunddata> {
        if (Date.now() - this.lastGrunddataFetch > (maxAge * 1000)) {
            this.apiClient.get<ILeverantorsorderDetailGrunddata>("Leverantorsorder/GetLeverantorsorderGrunddata", false).subscribe(r => {
                this.grunddataObservable.next(r);
                this.lastGrunddataFetch = Date.now();
            });
        }
        return this.grunddataObservable;
    }

    getPeriodFrom(rader: ILeverantorsorderDetailRad[]) {
        return Math.min.apply(null, rader.filter(r => r.periodFrom !== undefined).map(rad => new Date(rad.periodFrom)));
    }

    getPeriodTom(rader: ILeverantorsorderDetailRad[]) {
        return Math.max.apply(null, rader.filter(r => r.periodTom !== undefined).map(rad => new Date(rad.periodTom)));
    }

    get(id: number): Observable<ILeverantorsorderDetail> {
        return this.apiClient.get<ILeverantorsorderDetail>(`Leverantorsorder/GetLeverantorsorderById/${id}`);
    }
}
